import { Box, Paper, Stack, styled, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { compact, isEmpty, keyBy, uniqBy } from 'lodash';
import { ComponentProps, ReactNode, useMemo, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';

import { SelectChevronIcon } from '@/components/form/baseInputs/SelectChevronIcon';
import { Loader } from '@/components/progress/Loader/Loader';
import { MenuItemSecondaryTypography } from '@/components/typography/MenuItemSecondaryTypography';
import { MenuItemTypography } from '@/components/typography/MenuItemTypography';
import { useStableValue } from '@/hooks/useStableValue';
import { COLORS } from '@/styles/tokens/colors';
import { truncateToLength } from '@/utils/stringUtils';

import { Chip } from '../../Chip/Chip';
import { Checkbox } from '../Checkbox';
import { FormControl } from '../FormControl';
import {
  BaseTypeaheadMultiSelectInputProps,
  FormControlTypeaheadMultiSelectInputProps,
  HelpTextVariant,
  SelectInputOption,
  TypeaheadSelectInputOption,
} from '../inputTypes';
import { SelectItemGroupLabel } from '../SelectInput/SelectItemGroupLabel';
import { TypeaheadOptionEndContent } from '../TypeaheadOptionEndContent';
import { TypeaheadPopperFooter } from '../TypeaheadPopperFooter';

const GroupItems = styled('ul')({
  padding: 0,
});

const useStyles = makeStyles()((theme) => ({
  root: {
    '.MuiAutocomplete-inputRoot': {
      // MUI always sets 36px of right padding for the popup indicator; override
      // since we use our own endAdornment instead.
      paddingRight: '10px !important',
      // Selections are always collapsed into a single chip, so the row never
      // needs to wrap. Without this, a long chip pushes the text input (which
      // MUI gives a 30px min-width) and the chevron onto a second line and
      // the field becomes two lines tall.
      flexWrap: 'nowrap',
    },
    '.MuiAutocomplete-tag': {
      margin: 0,
      marginRight: theme.spacing(1),
      // Allow the chip to shrink below its content width (flex items default
      // to min-width: auto) so its label ellipsizes instead of overflowing
      // the row.
      minWidth: 0,
    },
    '.MuiAutocomplete-input': {
      padding: '0px !important',
    },
  },
}));

const DEFAULT_MAX_DISPLAY_LENGTH = 35;

function getTagDisplay(
  tagValues: TypeaheadSelectInputOption<unknown>[],
  maxLength = DEFAULT_MAX_DISPLAY_LENGTH
) {
  const [firstTagValue, ...otherTagValues] = tagValues;
  const numberOfOtherTags = otherTagValues.length;

  // If there are no other tags, just return the first tag's display value, truncated to the max length.
  if (numberOfOtherTags === 0) {
    return truncateToLength(firstTagValue?.display ?? '', maxLength);
  }

  // If there are other tags, return the first tag's display value, truncated to the max length,
  // and add a summary of the other tags.
  const otherTagsSummary = `+${numberOfOtherTags} more`;
  // -1 for the space between the first tag and the other tags summary
  const maxFirstTagLength = maxLength - otherTagsSummary.length - 1;

  const firstTagDisplay = truncateToLength(
    firstTagValue?.display ?? '',
    maxFirstTagLength
  );

  return `${firstTagDisplay} ${otherTagsSummary}`;
}

const BaseTypeaheadMultiSelectInput = ({
  options,
  testId,
  required,
  inputRef,
  noOptionsText,
  inputValue,
  onInputChange,
  startAdornment,
  endAdornment,
  values,
  maxTagDisplayLength,
  popperFooter,
  filterOptions,
  inlineEndLabel,
  ...inputProps // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: BaseTypeaheadMultiSelectInputProps<any>) => {
  const { classes } = useStyles();
  const { disabled } = inputProps;
  // explicitly splitting off error and autocomplete so we don't pass them to
  // the top-level Autocomplete element, which doesn't accept them
  const { autoComplete, error, ...otherInputProps } = inputProps;
  // MUI resets in-progress search text when controlled values change by
  // reference. Consumers often derive this array during render, so stabilize
  // it here instead of requiring a workaround at each caller.
  const stableValues = useStableValue(values ?? []);

  // Set of currently-selected option values, used to derive the checked /
  // indeterminate state of synthetic parent rows (whose own value is never in
  // `values`, so MUI's `state.selected` is always false for them).
  const selectedValueSet = useMemo(
    () => new Set(stableValues.map((v) => v.value)),
    [stableValues]
  );

  // MUI compares PaperComponent by reference: any new component type causes
  // the autocomplete popper (and its option list) to unmount/remount, losing
  // scroll position and hover/keyboard state. Keep the component stable for
  // the lifetime of the input by reading the current footer through a ref;
  // its identity changes (e.g. when a memoized footer's deps change due to
  // a selection update) won't trigger a remount.
  const popperFooterRef = useRef(popperFooter);
  popperFooterRef.current = popperFooter;
  const hasFooter = popperFooter != null;
  const PaperComponent = useMemo(() => {
    if (!hasFooter) return undefined;
    return function PaperWithFooter({
      children,
      ...paperProps
    }: ComponentProps<typeof Paper>) {
      return (
        <Paper {...paperProps}>
          {children}
          <TypeaheadPopperFooter
            onMouseDown={(e) => {
              // Prevent the autocomplete from stealing focus / closing
              // when the user interacts with the footer button.
              e.preventDefault();
            }}
          >
            {popperFooterRef.current}
          </TypeaheadPopperFooter>
        </Paper>
      );
    };
  }, [hasFooter]);
  const disabledStyle = disabled ? { background: COLORS.GRAY[50] } : {};
  const mergedInputSx = Object.assign(
    {
      background: 'white',
      m: 0,
      alignItems: 'center',
    },
    disabledStyle
  );

  return (
    // this select is a non-optimal experience for mobile; consider using NativeSelect here for a mobile device
    <Autocomplete<TypeaheadSelectInputOption<unknown>, true, boolean>
      multiple
      autoHighlight
      openOnFocus
      disableClearable
      className={classes.root}
      value={stableValues}
      {...otherInputProps}
      disableCloseOnSelect
      onChange={(event, selectedOptions, reason, details) => {
        const actionOption = selectedOptions.find(
          (option) => option.onSelectAction
        );

        if (actionOption?.onSelectAction) {
          actionOption.onSelectAction();
          return;
        }

        otherInputProps.onChange?.(event, selectedOptions, reason, details);
      }}
      options={options}
      filterOptions={filterOptions}
      inputValue={inputValue}
      onInputChange={(_, value, reason) => {
        // MUI emits an empty selectOption/reset update after a multiple
        // selection; retain an active search until the user edits it or the
        // field loses focus.
        if (
          (reason === 'selectOption' || reason === 'reset') &&
          value === '' &&
          inputValue !== ''
        ) {
          return;
        }

        onInputChange?.(value, reason);
      }}
      onBlur={() => onInputChange?.('', 'clear')} // for some reason, clearOnBlur doesn't actually fire a clear event
      noOptionsText={noOptionsText ?? 'No options'}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      renderOption={({ key: key, ...props }, option, state) => {
        if (option.renderOption) {
          return option.renderOption(props);
        }

        // Synthetic parent rows derive their checkbox state from how many of
        // their children are selected (all → checked, some → indeterminate),
        // since the parent's own value is never written to `values`.
        let checked = state.selected;
        let indeterminate = false;
        if (option.isParentRow) {
          const childValues = option.childValues ?? [];
          const selectedChildCount = childValues.filter((value) =>
            selectedValueSet.has(value)
          ).length;
          checked =
            childValues.length > 0 && selectedChildCount === childValues.length;
          indeterminate = selectedChildCount > 0 && !checked;
        }
        const indentLevel = option.indentLevel ?? 0;
        const checkboxNode = (
          <Checkbox
            value={checked}
            indeterminate={indeterminate}
            label={
              <Stack
                alignItems="center"
                width="100%"
                direction="row"
                flexGrow={1}
                justifyContent="space-between"
                spacing={0.5}
              >
                <Stack direction="column" spacing={0} flex={1}>
                  <MenuItemTypography>
                    {option.display}
                    {inlineEndLabel && option.endLabel
                      ? ` (${option.endLabel})`
                      : null}
                  </MenuItemTypography>
                  {option.secondaryText && (
                    <MenuItemSecondaryTypography>
                      {option.secondaryText}
                    </MenuItemSecondaryTypography>
                  )}
                </Stack>
                <TypeaheadOptionEndContent
                  endLabel={option.endLabel}
                  badgeText={option.badgeText}
                  endAdornment={option.endAdornment}
                  showEndLabel={!inlineEndLabel}
                />
              </Stack>
            }
          />
        );
        return (
          <Box component="li" key={option.value || key} {...props}>
            {indentLevel > 0 ? (
              <Stack direction="row" flexGrow={1} sx={{ pl: indentLevel * 3 }}>
                {checkboxNode}
              </Stack>
            ) : (
              checkboxNode
            )}
          </Box>
        );
      }}
      renderGroup={(params) => (
        <li key={params.key}>
          {params.group && <SelectItemGroupLabel label={params.group} />}
          <GroupItems>{params.children}</GroupItems>
        </li>
      )}
      renderTags={(tagValues, getTagProps) => {
        if (tagValues.length === 1) {
          const option = tagValues[0];
          if (!option) return null;

          const { key, onDelete, ...props } = getTagProps({ index: 0 });
          return (
            <Chip
              key={key}
              label={truncateToLength(
                option.display,
                maxTagDisplayLength ?? DEFAULT_MAX_DISPLAY_LENGTH
              )}
              {...props}
              disabled={option.disabled || disabled}
              onRemove={onDelete}
              data-testid="TypeaheadMultiSelect-tag"
            />
          );
        }

        const tagDisplayValue = getTagDisplay(tagValues, maxTagDisplayLength);
        const {
          key,
          onDelete: _onDelete,
          ...tagProps
        } = getTagProps({
          index: 0,
        });

        return (
          <Chip
            key={key}
            label={tagDisplayValue}
            {...tagProps}
            disabled={tagValues.some((option) => option.disabled) || disabled}
            onRemove={(e) => otherInputProps.onChange?.(e, [], 'clear')}
          />
        );
      }}
      // this method name is misleading. it actually passes the whole option when the
      // selection popover is opened, and just the value when the popover is closed.
      // see: https://github.com/mui/material-ui/issues/31192
      getOptionLabel={(valueOrOption) => valueOrOption.display}
      getOptionDisabled={(option) => !!option.disabled}
      PaperComponent={PaperComponent}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            inputRef={inputRef}
            variant="outlined"
            error={error}
            placeholder={isEmpty(values) ? inputProps.placeholder : ''}
            InputProps={{
              ...params.InputProps,
              // In multiple mode, MUI puts the selection chips in
              // `startAdornment`. Keep the caller's adornment before the chips.
              startAdornment: startAdornment ? (
                <>
                  {startAdornment}
                  {params.InputProps.startAdornment}
                </>
              ) : (
                params.InputProps.startAdornment
              ),
              endAdornment,
              sx: mergedInputSx,
              inputProps: {
                ...params.inputProps,
                required,
                autoComplete,
                'data-testid':
                  testId ?? `select-input-${inputProps.name ?? ''}`,
              },
            }}
          />
        );
      }}
    />
  );
};

export interface TypeaheadMultiSelectInputProps
  extends FormControlTypeaheadMultiSelectInputProps<string> {
  label: string;
  // the presence of contextualHelp indicates that there's help text related to this input, which
  // will cause the "info" icon to show up next to the input label
  contextualHelp?: JSX.Element;
  errorMessage?: string;
  helpText?: string;
  helpTextVariant?: HelpTextVariant;
  hideLabel?: boolean;
  autoSelect?: boolean;
  loading?: boolean;
  maxTagDisplayLength?: number;
  popperFooter?: ReactNode;
  labelIconEnd?: ReactNode;
  /** Keep selected options in their original groups when false. */
  groupSelectedOptions?: boolean;
}

export function TypeaheadMultiSelectInput({
  id,
  label,
  contextualHelp,
  errorMessage,
  helpText,
  helpTextVariant,
  hideLabel,
  loading,
  options,
  labelIconEnd,
  groupSelectedOptions = true,
  ...inputProps
}: TypeaheadMultiSelectInputProps) {
  const { values: selectedValues } = inputProps;

  const finalOptions = useMemo(() => {
    if (!groupSelectedOptions) return options;
    const optionsByValue = keyBy(options, 'value');

    // When the option list is hierarchical (parent rows with nested child
    // rows), pin selected items while keeping each parent group intact. A
    // parent is pinned to "Selected" (with its children) only when ALL its
    // children are selected; a partially-selected parent stays whole in its
    // home group so its unselected children keep their parent row above them.
    // Standalone leaves pin as before. Only take this path when parent rows are
    // present, so non-hierarchical consumers are unaffected.
    const hasParentRows = options.some((o) => o.isParentRow);

    if (hasParentRows) {
      const selectedValueSet = new Set(
        selectedValues?.map((v) => v.value) ?? []
      );
      const isParentFullySelected = (
        parentValue: string | undefined
      ): boolean => {
        if (parentValue == null) return false;
        const childValues = optionsByValue[parentValue]?.childValues;
        return (
          !!childValues?.length &&
          childValues.every((c) => selectedValueSet.has(c))
        );
      };

      // Iterate in option order so a pinned parent stays immediately followed
      // by its children (parents precede their children in `options`).
      const selectedBlock: TypeaheadSelectInputOption<string>[] = [];
      for (const opt of options) {
        if (opt.isParentRow) {
          if (isParentFullySelected(opt.value)) {
            selectedBlock.push({ ...opt, groupName: 'Selected' });
          }
        } else if (opt.parentValue != null) {
          if (isParentFullySelected(opt.parentValue)) {
            selectedBlock.push({ ...opt, groupName: 'Selected' });
          }
        } else if (selectedValueSet.has(opt.value)) {
          selectedBlock.push({ ...opt, groupName: 'Selected' });
        }
      }

      return uniqBy<SelectInputOption<string>>(
        compact([...selectedBlock, ...options]),
        'value'
      );
    }

    // group all the selected options at the top under the label "Selected"
    const selectedOptions =
      selectedValues?.flatMap((selectedValue) => {
        const option = optionsByValue[selectedValue.value];
        return option
          ? {
              ...option,
              groupName: 'Selected',
            }
          : [];
      }) ?? [];

    // we want to make sure to always include both the current query options *and* the option that corresponds
    // to the current value, but don't include the same option more than once
    // this will be a no-op if selectedOptions is empty
    return uniqBy<SelectInputOption<string>>(
      compact([
        // put the currently-selected options first in the list
        ...selectedOptions,
        ...options,
      ]),
      'value'
    );
  }, [options, selectedValues, groupSelectedOptions]);

  const inputValue = useMemo(() => {
    return inputProps.inputValue ?? '';
  }, [inputProps.inputValue]);

  return (
    <FormControl<BaseTypeaheadMultiSelectInputProps<string>>
      id={id}
      hideLabel={hideLabel}
      contextualHelp={contextualHelp}
      component={BaseTypeaheadMultiSelectInput}
      label={label}
      labelIconEnd={labelIconEnd}
      helpText={helpText}
      helpTextVariant={helpTextVariant}
      required={inputProps.required}
      errorMessage={errorMessage}
      inputProps={{
        ...inputProps,
        values: selectedValues,
        groupBy: (option) => option.groupName ?? '',
        inputValue,
        options: finalOptions,
        noOptionsText: loading ? 'Loading...' : inputProps.noOptionsText,
        endAdornment: (
          <>
            {loading && (
              <Loader
                boxProps={{
                  alignItems: 'center',
                  position: 'absolute',
                  right: 10,
                  top: 12,
                  zIndex: 1,
                }}
                circularProgressProps={{ size: 15 }}
              />
            )}
            {inputProps.endAdornment ?? (
              <SelectChevronIcon disabled={inputProps.disabled} />
            )}
          </>
        ),
      }}
    />
  );
}
